/**
 * AdminStorageTab — OneDrive File Manager (Test)
 *
 * Test page for browsing, uploading, downloading, and deleting
 * files from the current user's connected OneDrive accounts.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  RefreshCw,
  Upload,
  Download,
  Trash2,
  Folder,
  File as FileIcon,
  ChevronRight,
  Cloud,
} from 'lucide-react';
import { toast } from 'sonner';
import { FileService, OneDriveFileItem } from '@/services/api/fileService';
import { api } from '@/services/api/client';
import * as errorUtils from '@/utils/errors';

interface StorageConnection {
  integrationId: string;
  isActive: boolean;
  config: {
    driveId: string;
    folderPath: string;
    tenantId: string;
    label: string;
  };
  lastConnectedAt?: string;
  createdAt?: string;
}

const extractLabel = (integrationId: string) => integrationId.replace('onedrive:', '');

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

export const AdminStorageTab: React.FC = () => {
  const [connections, setConnections] = useState<StorageConnection[]>([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState<OneDriveFileItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OneDriveFileItem | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch connected OneDrive accounts
  const fetchConnections = useCallback(async () => {
    setIsLoadingConnections(true);
    try {
      const response = await api.get<StorageConnection[]>('/storage-connections');
      if (response.success && response.data) {
        const active = response.data.filter((c) => c.isActive);
        setConnections(active);
        // Auto-select if only one account
        if (active.length === 1) {
          const label = extractLabel(active[0].integrationId);
          setSelectedLabel(label);
          setCurrentPath(active[0].config?.folderPath || '/TerraMatrix');
        }
      }
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to fetch storage connections'));
    }
    setIsLoadingConnections(false);
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Fetch files when label or path changes
  const fetchFiles = useCallback(async () => {
    if (!selectedLabel || !currentPath) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await FileService.browsePersonalOneDrive(selectedLabel, currentPath);
      if (response.success && response.data) {
        setFiles(response.data);
      } else {
        setFiles([]);
      }
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to list files'));
      setFiles([]);
    }
    setIsLoading(false);
  }, [selectedLabel, currentPath]);

  useEffect(() => {
    if (selectedLabel && currentPath) {
      fetchFiles();
    }
  }, [selectedLabel, currentPath, fetchFiles]);

  const handleAccountSelect = useCallback((label: string) => {
    setSelectedLabel(label);
    const conn = connections.find((c) => extractLabel(c.integrationId) === label);
    setCurrentPath(conn?.config?.folderPath || '/TerraMatrix');
    setFiles([]);
    setError(null);
  }, [connections]);

  // Breadcrumb path segments
  const pathSegments = useMemo(() => {
    if (!currentPath) return [];
    return currentPath.split('/').filter(Boolean);
  }, [currentPath]);

  const navigateToSegment = useCallback((segmentIndex: number) => {
    const segments = currentPath.split('/').filter(Boolean);
    setCurrentPath('/' + segments.slice(0, segmentIndex + 1).join('/'));
  }, [currentPath]);

  const navigateToFolder = useCallback((file: OneDriveFileItem) => {
    setCurrentPath(file.path);
  }, []);

  // Sort: folders first, then alphabetical
  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [files]);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLabel) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      await FileService.uploadToPersonalOneDrive(selectedLabel, file, currentPath, setUploadProgress);
      toast.success(`Uploaded ${file.name}`);
      await fetchFiles();
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Upload failed'));
      toast.error(errorUtils.getErrorMessageOrFallback(err, 'Upload failed'));
    }
    setIsUploading(false);
    setUploadProgress(0);
    e.target.value = '';
  }, [selectedLabel, currentPath, fetchFiles]);

  const handleDownload = useCallback(async (file: OneDriveFileItem) => {
    try {
      const response = await FileService.getPersonalOneDriveDownloadUrl(selectedLabel, file.path);
      if (response.success && response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        toast.error('Failed to get download URL');
      }
    } catch (err: unknown) {
      toast.error(errorUtils.getErrorMessageOrFallback(err, 'Download failed'));
    }
  }, [selectedLabel]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || !selectedLabel) return;
    try {
      await FileService.deleteFromPersonalOneDrive(selectedLabel, deleteTarget.path);
      toast.success(`Deleted ${deleteTarget.name}`);
      setDeleteTarget(null);
      await fetchFiles();
    } catch (err: unknown) {
      toast.error(errorUtils.getErrorMessageOrFallback(err, 'Delete failed'));
      setDeleteTarget(null);
    }
  }, [deleteTarget, selectedLabel, fetchFiles]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center gap-3 mb-3">
          <Cloud className="h-5 w-5 text-[#0078D4]" />
          <h3 className="text-lg font-semibold">OneDrive File Manager</h3>
          <Badge variant="secondary" className="text-xs">Test Tool</Badge>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Account selector */}
          <Select value={selectedLabel} onValueChange={handleAccountSelect}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select OneDrive account..." />
            </SelectTrigger>
            <SelectContent>
              {connections.map((c) => {
                const label = extractLabel(c.integrationId);
                return (
                  <SelectItem key={c.integrationId} value={label}>
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Breadcrumb */}
          {selectedLabel && pathSegments.length > 0 && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {pathSegments.map((segment, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
                  <button
                    className="hover:text-foreground hover:underline"
                    onClick={() => navigateToSegment(i)}
                  >
                    {segment}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={!selectedLabel || isUploading}
            >
              <Upload className="w-4 h-4 mr-1" />
              {isUploading ? `${uploadProgress}%` : 'Upload'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchFiles}
              disabled={!selectedLabel || isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 p-4">
          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded">{error}</div>
        </div>
      )}

      {/* File table */}
      <div className="flex-1 overflow-auto p-4">
        {isLoadingConnections ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            Loading connections...
          </div>
        ) : connections.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            No OneDrive accounts connected. Go to Settings to connect one.
          </div>
        ) : !selectedLabel ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            Select a OneDrive account to browse files
          </div>
        ) : isLoading && files.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            Loading files...
          </div>
        ) : files.length === 0 && !error ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            No files in this directory
          </div>
        ) : sortedFiles.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-muted text-left text-sm">
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium w-24">Size</th>
                  <th className="p-3 font-medium w-48">Modified</th>
                  <th className="p-3 font-medium w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedFiles.map((file) => (
                  <tr key={file.path} className="border-t hover:bg-muted/30 text-sm">
                    <td className="p-3">
                      {file.isFolder ? (
                        <button
                          className="flex items-center gap-2 hover:underline text-blue-600"
                          onClick={() => navigateToFolder(file)}
                        >
                          <Folder className="h-4 w-4 text-amber-500 shrink-0" />
                          {file.name}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                          {file.name}
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">
                      {file.isFolder ? '-' : formatBytes(file.size)}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {file.lastModified
                        ? new Date(file.lastModified).toLocaleString()
                        : '-'}
                    </td>
                    <td className="p-3">
                      {!file.isFolder && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => handleDownload(file)}
                            title="Download"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(file)}
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
